/**
 * Default display-name translations for field configurations.
 *
 * Keyed by field name, then by language code (excluding the default
 * language 'en', which is the base `displayName` on FieldConfig).
 *
 * When FieldConfigUtils creates a FieldConfig, it attaches these as
 * `_i18n` so that `localizeField` / `getLabel` resolve them automatically.
 * Admins can still override any label per-language via the flow editor —
 * these are just the starting defaults.
 *
 * To add a new language, add its code as a key under each field name.
 */
export const FIELD_LABEL_DEFAULTS: Record<string, Record<string, string>> = {

  // ── Borrower fields ──────────────────────────────────────────
  "borrower.firstName":                        { es: "Nombre" },
  "borrower.lastName":                         { es: "Apellido" },
  "borrower.middleName":                       { es: "Segundo nombre" },
  "borrower.nameSuffix":                       { es: "Sufijo" },
  "borrower.primaryEmail":                     { es: "Correo electrónico" },
  "borrower.mobilePhone":                      { es: "Número de teléfono" },
  "borrower.homePhone":                        { es: "Teléfono de casa" },
  "borrower.phoneType":                        { es: "Tipo de teléfono" },
  "borrower.dateOfBirth":                      { es: "Fecha de nacimiento" },
  "borrower.socialSecNum":                     { es: "Número de Seguro Social" },
  "borrower.declarations.residenceStatus":     { es: "Ciudadanía" },
  "borrower.maritalStatus":                    { es: "Estado civil" },
  "borrower.numberOfDependents":               { es: "Número de dependientes" },
  "borrower.languagePreference":               { es: "Preferencia de idioma" },
  "borrower.languagePreferenceOtherDescription": { es: "Especificar idioma" },
  "borrower.dependentAges":                    { es: "Edades de los dependientes" },
  "borrower.unmarriedAddendum":                { es: "Tiene pareja no casada con derechos de propiedad similares a los de un cónyuge legal" },
  "borrower.domesticRelationshipType":         { es: "Tipo de relación" },
  "borrower.unmarriedRelationshipState":       { es: "Estado de la relación" },
  "borrower.unmarriedRelationshipTypeOther":   { es: "Especifique el tipo de relación" },

  // ── Address / Residency fields ───────────────────────────────
  "residencyAddress.durationYears":            { es: "Año(s)" },
  "residencyAddress.durationMonths":           { es: "Mes(es)" },
  "residencyAddress.residencyBasis":           { es: "Estado de propiedad" },
  "residencyAddress.rent":                     { es: "Pago mensual" },
  "residencyAddress.moveInDate":               { es: "Fecha de mudanza" },

  // ── Declaration explanation fields ───────────────────────────
  "borrower.declarations.borrowedDownPayment.explanation":                  { es: "Explicación del pago inicial prestado" },
  "borrower.declarations.coSignedLoan.explanation":                         { es: "Explicación de préstamo co-firmado" },
  "borrower.declarations.haveJudgement.explanation":                        { es: "Explicación de sentencias pendientes" },
  "borrower.declarations.haveTaxDebt.explanation":                          { es: "Explicación de deuda federal en mora" },
  "borrower.declarations.haveLawsuit.explanation":                          { es: "Explicación de demanda" },
  "borrower.declarations.haveConveyedTitleInLieuOfForeclosure.explanation": { es: "Explicación de cesión de título en lugar de ejecución hipotecaria" },
  "borrower.declarations.haveCompletedShortSale.explanation":               { es: "Explicación de venta corta / pre-ejecución" },
  "borrower.declarations.beenForclosed.explanation":                        { es: "Explicación de ejecución hipotecaria" },
  "borrower.declarations.declaredBankruptcy.explanation":                   { es: "Explicación de quiebra" },

  // ── Refinance fields (URLA 2020 — Loan & Property Info) ─────
  "subjectProperty.refiPurpose":                  { es: "Tipo de refinanciamiento" },
  "subjectProperty.refinancePrimaryPurpose":      { es: "Propósito del refinanciamiento" },

  // ── Employment / Income fields ───────────────────────────────
  "employment.businessType":                   { es: "Tipo de negocio" },
  "employment.employer":                       { es: "Nombre del empleador" },
  "employment.employer.address.address1":      { es: "Dirección" },
  "employment.employer.address.city":          { es: "Ciudad" },
  "employment.employer.address.state":         { es: "Estado" },
  "employment.employer.address.zipCode":       { es: "Código postal" },
  "employment.employer.address.country":       { es: "País" },
  "employment.employer.address.county":        { es: "Condado" },
  "employment.isPrimary":                      { es: "Tipo de empleo" },
  "employment.position":                       { es: "Posición" },
  "employment.employmentType":                 { es: "Actual o anterior" },
  "employment.employerPhone":                  { es: "Teléfono corporativo" },
  "employment.yearsInLineOfWork":              { es: "Año(s) en la profesión" },
  "employment.startDate":                      { es: "Fecha de inicio" },
  "employment.endDate":                        { es: "Fecha de finalización" },
  "employment.selfEmploymentMonthlyIncomeOrLoss": { es: "Ingreso/pérdida mensual" },
  "employment.borrowerOwnershipShare":         { es: "Participación en la propiedad" },
  "employment.borrowerOwnershipSharePercent":  { es: "Porcentaje de propiedad" },
  "employment.selfEmploymentForm":             { es: "Formulario de trabajo independiente" },
  "employment.selfEmployed":                   { es: "Trabajo independiente" },
  "monthlyPay":                                { es: "Pago mensual" },
  "income.typeOfIncomeOtherDescription":       { es: "Descripción" },
  "income.monthlyIncome":                      { es: "Ingreso mensual" },
  "income.monthlyRent":                        { es: "Renta mensual" },

  // ── Asset fields ─────────────────────────────────────────────
  "asset.assetType":                           { es: "Tipo de activo" },
  "asset.depository":                          { es: "Nombre del banco/cooperativa" },
  "asset.cashMarketValue":                     { es: "Monto" },
  "asset.accountNumber":                       { es: "Número de cuenta" },
  "asset.makeModel":                           { es: "Marca/modelo" },
  "asset.year":                                { es: "Año" },
  "asset.disposition":                         { es: "Disposición" },
  "asset.amountOfMortgageLien":                { es: "Monto del gravamen hipotecario" },
  "asset.mortgagePayment":                     { es: "Pago hipotecario" },
  "asset.gross":                               { es: "Bruto" },
  "asset.netRentalIncome":                     { es: "Ingreso neto por alquiler" },
  "asset.insMainTaxMisc":                      { es: "Seguro, mantenimiento, impuestos, otros" },
  "asset.giftOrGrantSource":                   { es: "Fuente" },
  "asset.isDownPaymentSource":                 { es: "¿Este regalo en efectivo se utilizará para el enganche?" },

  // ── REO fields ───────────────────────────────────────────────
  "reo.typeOfProperty":                        { es: "Tipo de propiedad" },
  "reo.marketValue":                           { es: "Valor de la propiedad" },
  "reo.currentUsageType":                      { es: "¿Cómo usa actualmente esta propiedad?" },
  "reo.intendedUsageType":                     { es: "¿Cómo planea usar esta propiedad?" },
  "reo.dispositionStatus":                     { es: "¿Planea vender esta propiedad?" },
  "reo.amountOfMortgage":                      { es: "¿Saldo actual de gravámenes sobre esta propiedad?" },
  "reo.mortgagePayment":                       { es: "Pago mensual" },
  "reo.grossRentalIncome":                     { es: "Ingreso bruto por alquiler" },
  "reo.purchasePrice":                         { es: "Precio de compra original" },
  "reo.dateAcquired":                          { es: "Fecha de adquisición" },
  "reo.hazardInsurance":                       { es: "Seguro de vivienda" },
  "reo.propertyTax":                           { es: "Impuesto sobre la propiedad" },
  "reo.hoa":                                   { es: "HOA" },
  "reo.monthlyMiscExpenses":                   { es: "Gastos mensuales varios" },

  // ── Purchase Credits / Loan Info fields ──────────────────────
  "subjectProperty.presentValue":              { es: "Valor estimado" },
  // LLS-218 — Type of Loan step keys (prefixed) so the config-driven labels keep their Spanish translation.
  "mortgage.subjectProperty.presentValue":     { es: "Valor estimado" },
  "mortgage.mortgageTerm.appraisedValue":      { es: "Valor de tasación" },
  "subjectProperty.propertyWillBe":            { es: "La propiedad será" },
  "subjectProperty.state":                     { es: "Estado" },
  "subjectProperty.yearBuilt":                 { es: "Año de construcción" },
  "subjectProperty.noOfUnits":                 { es: "Número de unidades" },
  "mortgageApplication.extension.purchaseAgreementContractSignedDate":
    { es: "¿Cuándo se firmó el contrato?" },
  "mortgageApplication.extension.purchaseAgreementExpectedCOEDate":
    { es: "¿Cuándo se espera el cierre?" },
  "mortgageApplication.transactionDetail.purchaseCredits.earnestMoneyDeposit":
    { es: "¿Cuánto es el depósito de garantía?" },
  "mortgageApplication.transactionDetail.purchaseCredits.sellerCredits":
    { es: "¿Cuál es el monto?" },
  "mortgageApplication.mortgageTerm.mortgageAppliedFor":
    { es: "Si lo tiene, selecciónelo de la lista. Si no, haga clic en continuar." },
  "mortgage.transactionDetail.purchasePriceAmount": { es: "Precio de compra" },
  "mortgage.transactionDetail.downpayment":    { es: "Enganche" },
  "mortgage.subjectProperty.downPaymentSource": { es: "Fuente del enganche" },

  // ── Manufactured Home fields ─────────────────────────────────
  "subjectProperty.manufacturedHomeMake":       { es: "Marca" },
  "subjectProperty.manufacturedHomeModel":      { es: "Modelo" },
  "subjectProperty.manufacturedHomePriceOfHome": { es: "Precio de la vivienda" },
  "subjectProperty.manufacturedHomeSalesPrice": { es: "Precio de venta" },
  "subjectProperty.manufacturedHomeLotOwnershipType": { es: "Tipo de propiedad del terreno" },
  "subjectProperty.manufacturedHomeLotRentalAmount":  { es: "Monto de renta del terreno" },
  "subjectProperty.landType":                  { es: "Tipo de terreno" },
  "subjectProperty.sizeOfLand":                { es: "Tamaño del terreno" },
  "subjectProperty.estimatedLandValue":        { es: "Valor estimado del terreno" },
  "subjectProperty.amountOwned":               { es: "Monto adeudado" },

  // ── Review Application fields ────────────────────────────────
  // "mortgage.transactionDetail.purchasePriceAmount" already defined above

  // ── MyInfoFieldConfig (short-name keys) ──────────────────────
  "ssn":                                       { es: "Número de Seguro Social" },
  "firstName":                                 { es: "Nombre" },
  "lastName":                                  { es: "Apellido" },
  "phoneNumber":                               { es: "Número de teléfono" },
  "citizenship":                               { es: "Ciudadanía" },
  "birthDate":                                 { es: "Fecha de nacimiento" },
  "maritalStatus":                             { es: "Estado civil" },
};
